import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Device } from '../../lib/types';
import type { SortDir } from '../../lib/types';
import { findFosterHomeForDevice, type FosterHome } from '../../lib/fosterAdapters';
import { useRescueContext } from '../../lib/RescueContext';
import ScheduledHandoffsModal from './ScheduledHandoffsModal';
import { formatScheduledFor, type ScheduledFor } from '../../lib/scheduling';
import { useToast } from '../../components/Toast/ToastContext';
import {
  RescueDeviceStatsData,
  rescueDeviceStatsQuery,
  RescueDevicesProgramData,
  rescueDevicesProgramQuery,
  RescueOrderShipmentsData,
  rescueOrderShipmentsQuery,
  rescueCreatePetFosterTransferMutation,
  rescueScheduleHandoffMutation,
  rescueRecallPetFosterTransferMutation,
  unassignDeviceMutation,
  rescueCancelPetAdoptionTransferMutation,
  rescueMarkAdoptionTransferNotRecoveredMutation,
  rescueMarkAdoptionTransferRecoveredMutation,
  rescueMarkAdoptionTransferStalledMutation,
  rescueSendSmsMutation,
  rescueCreateFosterHomeMutation,
  rescueArchiveFosterHomeMutation,
  type RescueCreateFosterHomeInput,
  type RescueCreateFosterHomeData,
  rescueUnassignedDeviceInventoryQuery,
  RescueUnassignedDeviceInventoryData,
} from '../../lib/rescueQueries';
import useRescueDevices from '../../hooks/useRescueDevices';
import useAllRescueDevices from '../../hooks/useAllRescueDevices';
import useAllPendingAdoptions from '../../hooks/useAllPendingAdoptions';
import useClientPagination from '../../hooks/useClientPagination';
import useDrainAllPages from '../../hooks/useDrainAllPages';
import useAdoptedPetIds from '../../hooks/useAdoptedPetIds';
import useFosterRoster from '../../hooks/useFosterRoster';
import { isTransferStalled, isFosterContactComplete } from '../../lib/utils';
import { sortDevices, type DeviceSort } from '../../lib/deviceSorting';
import SearchBar from '../../components/SearchBar/SearchBar';
import Button from '../../components/Button/Button';
import PortalTabs, { TabDef } from '../../components/PortalTabs/PortalTabs';
import StockTable from './StockTable';
import DeviceSectionTable from './DeviceSectionTable';
import type { DeviceSortKey } from './DeviceSectionTable';
import DevicesInTransitTable from './DevicesInTransitTable';
import PendingTransfersTable from './PendingTransfersTable';
import type { PendingSortKey } from './PendingTransfersTable';
import StalledTransfersTable from './StalledTransfersTable';
import type { StalledSortKey } from './StalledTransfersTable';
import MarkRecoveredModal from './MarkRecoveredModal';
import NotRecoveredModal from './NotRecoveredModal';
import CompletedTransfersTable from './CompletedTransfersTable';
import AddPetModal from './AddPetModal';
import AddDeviceToPetModal from './AddDeviceToPetModal';
import TransferToFosterModal, {
  type FosterTransferInput,
} from '../../components/TransferToFosterModal/TransferToFosterModal';
import RecallDeviceModal from '../../components/RecallDeviceModal/RecallDeviceModal';
import AdoptFromFosterModal from './AdoptFromFosterModal';
import FosterRosterTable from './FosterRosterTable';
import FosterHomeDetailsModal from './FosterHomeDetailsModal';
import ViewPetModal from '../Dashboard/ViewPetModal';
import AddFosterModal from './AddFosterModal';
import TransferToAdopterModal from '../../components/TransferToAdopterModal/TransferToAdopterModal';
import ResendEmailModal from './ResendEmailModal';
import CancelTransferModal, { CancellationReason } from './CancelTransferModal';
import WrongAdopterInfoModal from './WrongAdopterInfoModal';
import DeviceReturnedModal from './DeviceReturnedModal';
import DeviceKeptModal from './DeviceKeptModal';
import TestingCancelModal from './TestingCancelModal';
import PetAndDeviceReturnedCancelModal from './PetAndDeviceReturnedCancelModal';
import SendSmsModal from './SendSmsModal';
import OrderDevicesModal from './OrderDevicesModal';
import DevicesEnrollModal from './DevicesEnrollModal';
import EnrollEmptyState from '../../components/EnrollEmptyState/EnrollEmptyState';
import {
  trackDeviceTransferToFoster,
  trackDeviceTransferToAdopt,
  trackDeviceRecalled,
  trackDeviceResendEmail,
  trackDeviceCancelTransfer,
  trackDeviceCancelTransferFailed,
  trackDeviceMarkedRecovered,
  trackDeviceMarkedNotRecovered,
  trackDeviceMarkedStalled,
  trackDeviceSmsSent,
  trackDevicesOrderClicked,
  trackChipOwnerAdded,
  trackFosterHomeCreated,
  trackFosterHomeArchived,
  trackHandoffScheduled,
} from '../../analytics';
import { useTransferPetWithChipToAdopter } from '../../lib/useTransferPetWithChipToAdopter';
import {
  refetchAllOpts,
  drainNeutralOpts,
  pendingResolveOpts,
  fosterRecallOpts,
  adoptOutOpts,
} from './drainedMutationOptions';
import styles from './Devices.module.scss';

/** Page size for the inventory drain loop. Server clamps to its own max. */
const INVENTORY_PAGE_SIZE = 100;

/** Default page size for the server-side paginated sections. */
const PAGE_SIZE = 10;

/**
 * How long typing settles before the search reaches the queries. The one debounce on the page —
 * see `handleSearchChange` for why it is not repeated per hook.
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * A pending adoption transfer is considered "stalled" once it has been awaiting
 * adopter activation for at least this many days. Stalled transfers are split
 * out of the normal Pending Transfers section into their own section.
 */
const STALLED_TRANSFER_DAYS = 7;

/**
 * The Fosters device section opens on newest placement first — the pets that moved most
 * recently are the ones a volunteer is still following up on. The user can re-sort from
 * any of that table's headers; there is no unsorted state to return to.
 */
const DEFAULT_FOSTER_SORT: DeviceSort = { key: 'placed', dir: 'desc' };

/** Show completed adoptions from the last 90 days. */
const COMPLETED_SINCE = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString();
})();

/** Top-level tabs. */
type DeviceTab = 'devices' | 'fosters' | 'adoptions';

const DEVICE_TABS: DeviceTab[] = ['devices', 'fosters', 'adoptions'];

/** Reads the `tab` query param, falling back to Devices for anything unrecognized. */
function parseDeviceTab(value: string | null): DeviceTab {
  return DEVICE_TABS.find((tab) => tab === value) ?? 'devices';
}

/** Sub-filters for the Devices tab, narrowing which panels are shown. */
type DeviceFilter = 'all' | 'stock' | 'activated' | 'inTransit';

const DEVICE_FILTERS: { key: DeviceFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'activated', label: 'Activated' },
  { key: 'stock', label: 'Stock' },
  { key: 'inTransit', label: 'In Transit' },
];

function LoadingSpinner({ message }: { message?: string }) {
  return (
    <div className={styles.loadingContainer}>
      <svg className={styles.spinner} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
        <path fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
      </svg>
      {message && <span>{message}</span>}
    </div>
  );
}

export default function Devices() {
  const { orgName } = useRescueContext();
  // `cache-and-network` — matching the Dashboard's card — because `pendingDeviceOrder` gates the
  // Order Devices button and only ops clears it, off in Slack. Under cache-first a rescue who was
  // blocked stays blocked on this page until a hard reload, long after their order was approved.
  const {
    data: enrollmentData,
    loading: enrollmentLoading,
    refetch: refetchEnrollment,
  } = useQuery<RescueDevicesProgramData>(rescueDevicesProgramQuery, { fetchPolicy: 'cache-and-network' });
  // Under `cache-and-network` every revalidation re-raises `loading` even though cached data is
  // already on screen, so "still loading" has to mean "loading with nothing to show" — otherwise a
  // background refetch flashes the page spinner and closes the enroll modal mid-revalidate.
  const enrollmentPending = enrollmentLoading && !enrollmentData;
  const isEnrolled = enrollmentData?.rescueDevicesProgram.enrolled ?? false;
  // An order the rescue has filed that Fi hasn't approved or rejected yet. Only one may be
  // outstanding, so while this is set the order CTA is closed off and the header explains why —
  // `rescueCreateDeviceOrder` would reject a second order anyway, and a form that can't be filed is
  // worse than no form.
  const pendingDeviceOrder = enrollmentData?.rescueDevicesProgram.pendingDeviceOrder ?? null;
  const { showToast } = useToast();
  const location = useLocation();
  const history = useHistory();
  const urlParams = new URLSearchParams(location.search);
  const urlSearch = urlParams.get('search') || '';
  const urlTab = parseDeviceTab(urlParams.get('tab'));
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [currentTab, setCurrentTab] = useState<DeviceTab>(urlTab);
  const [deviceFilter, setDeviceFilter] = useState<DeviceFilter>('all');
  // Backs the roster section and the assign-to-foster picker, so only Adoptions can skip it.
  const roster = useFosterRoster({ enabled: currentTab !== 'adoptions', visible: currentTab === 'fosters' });
  const fosterHomes = roster.homes;
  const { dismissOverlays: dismissRosterOverlays } = roster;
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Sync local input when URL changes externally (e.g. navigating from dashboard). Also drop the
  // roster overlays: a pet profile's device card links back to this page with a search on it, and
  // the row it lands on would otherwise sit behind the modal that opened it.
  useEffect(() => {
    setSearchInput(urlSearch);
    setSearchQuery(urlSearch);
    dismissRosterOverlays();
  }, [urlSearch, dismissRosterOverlays]);

  // Follow the `tab` param when the URL changes externally: the dashboard links a pet's device
  // here with the tab that lists it, since the Devices tab covers neither placements nor adoptions.
  useEffect(() => {
    setCurrentTab(urlTab);
  }, [urlTab]);

  function handleTabChange(key: string) {
    const tab = parseDeviceTab(key);
    setCurrentTab(tab);
    const params = new URLSearchParams(location.search);
    if (tab === 'devices') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    history.replace({ search: params.toString() });
  }

  // The page owns the only search debounce: `searchInput` drives the field, and `searchQuery`
  // — what every query on the page reads — lands one delay later, together with the URL param.
  // Neither the paginated hook nor the drains debounce again, so they all switch searches on
  // the same tick.
  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value);
      const params = new URLSearchParams(location.search);
      if (value) {
        params.set('search', value);
      } else {
        params.delete('search');
      }
      history.replace({ search: params.toString() });
    }, SEARCH_DEBOUNCE_MS);
  }

  // ── Org name (from context — resolves chipReseller or RESCUE household) ──

  const resellerName = orgName;

  // ── Stats (unfiltered counts for summary cards) ──

  const { data: statsData } = useQuery<RescueDeviceStatsData>(rescueDeviceStatsQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { completedSince: COMPLETED_SINCE },
  });
  const stats = statsData?.rescueDeviceStats;
  const scheduledCount = stats?.scheduledHandoffsCount ?? 0;

  // ── Outstanding shipments (Devices in Transit) ──

  const { data: shipmentsData } = useQuery<RescueOrderShipmentsData>(rescueOrderShipmentsQuery);
  const shipments = shipmentsData?.rescueOrderShipments ?? [];

  // ── Unassigned Device Inventory ───────────
  //
  // The `rescueUnassignedDeviceInventory` query returns only delivered devices
  // not attached to a pet — server-filtered. We still page through with
  // fetchMore so the Unassigned section can show the full count even when the
  // server clamps a single page. Search is applied server-side via input.query
  // so the Unassigned section honors the search bar like Rescue/Foster do.

  const inventoryInput = useMemo(
    () => (searchQuery ? { query: searchQuery, limit: INVENTORY_PAGE_SIZE } : { limit: INVENTORY_PAGE_SIZE }),
    [searchQuery],
  );

  const {
    data: inventoryData,
    loading: inventoryLoading,
    error: inventoryError,
    refetch: refetchInventory,
    fetchMore: fetchMoreInventory,
  } = useQuery<RescueUnassignedDeviceInventoryData>(rescueUnassignedDeviceInventoryQuery, {
    variables: { input: inventoryInput },
    notifyOnNetworkStatusChange: true,
  });

  // Drain remaining pages whenever the cached result still has more, so the
  // Unassigned section can show the full count even when the server clamps a
  // single page. Scalar selectors keep the drain effect from re-running on
  // unrelated Apollo cache writes.
  const inventoryHasMore = inventoryData?.rescueUnassignedDeviceInventory?.hasMore ?? false;
  const inventoryItemsLength = inventoryData?.rescueUnassignedDeviceInventory?.items.length ?? 0;
  const inventoryTotalCount = inventoryData?.rescueUnassignedDeviceInventory?.totalCount ?? 0;

  const mergeInventoryItems = useCallback(
    (
      previous: RescueUnassignedDeviceInventoryData,
      fetchMoreResult: RescueUnassignedDeviceInventoryData,
    ): RescueUnassignedDeviceInventoryData => ({
      rescueUnassignedDeviceInventory: {
        ...fetchMoreResult.rescueUnassignedDeviceInventory,
        items: [
          ...previous.rescueUnassignedDeviceInventory.items,
          ...fetchMoreResult.rescueUnassignedDeviceInventory.items,
        ],
      },
    }),
    [],
  );

  useDrainAllPages<RescueUnassignedDeviceInventoryData>({
    input: inventoryInput,
    hasMore: inventoryHasMore,
    itemsLength: inventoryItemsLength,
    totalCount: inventoryTotalCount,
    fetchMore: fetchMoreInventory,
    mergeItems: mergeInventoryItems,
  });

  // ── Section sorting ───────────────────────
  //
  // The Activated (IN_RESCUE) section is server-paginated by default. Sorting by
  // Pet / Device spans the whole dataset, not just the current page — so the
  // first time the user clicks a sortable header we switch to a drained full
  // list and sort + paginate it client-side. `activatedSort` is null until then,
  // which keeps the drain query dormant (no extra network cost while unsorted).
  //
  // The Fosters section uses the same machinery but is *always* sorted: it opens
  // on newest placement first, which the server can't provide (it orders by pet
  // name), so that section reads from its drained list from the start and never
  // has a server-paginated mode to fall back to. Clicking any of its headers —
  // Placed On included — re-sorts the same full list.
  const [activatedSort, setActivatedSort] = useState<DeviceSort | null>(null);
  const [fosterSort, setFosterSort] = useState<DeviceSort>(DEFAULT_FOSTER_SORT);
  const activatedSortActive = activatedSort !== null;

  // ── Server-side paginated sections ────────
  //
  // While a sort is active the section is driven by its drained list below, so
  // skip the matching server-paginated query to avoid two live queries for the
  // same filter.
  const rescue = useRescueDevices('IN_RESCUE', searchQuery, PAGE_SIZE, resellerName, undefined, {
    skip: activatedSortActive,
  });
  const completed = useRescueDevices('COMPLETED_ADOPTIONS', searchQuery, PAGE_SIZE, resellerName, COMPLETED_SINCE);

  const {
    devices: drainedActivated,
    totalCount: drainedActivatedTotal,
    complete: drainedActivatedComplete,
    error: drainedActivatedError,
    refetch: refetchActivated,
  } = useAllRescueDevices('IN_RESCUE', searchQuery, resellerName, { skip: !activatedSortActive });

  // The Fosters section is always sorted, so it always reads from the drain — but draining is
  // many sequential requests for a large rescue, and only that tab renders the rows. So the
  // drain waits until the tab is first opened, and the badge count comes from a single-row
  // query instead of from every page.
  // Both are live while that tab is open, which the count query's own note explains — and
  // which is why `removeDrainedDevice` and `skipDrains` have to tell the two apart.
  const onFostersTab = currentTab === 'fosters';
  // Latched rather than tracking the tab. Un-skipping re-subscribes a cache-and-network
  // query whose watched variables are page one, and that response replaces the pages
  // fetchMore had accumulated — so toggling `skip` off the tab and back would collapse a
  // finished table to skeletons and re-drain it, every visit. Once drained, stay subscribed;
  // never visited, never drain.
  const [fostersEverOpened, setFostersEverOpened] = useState(onFostersTab);
  useEffect(() => {
    if (onFostersTab) {
      setFostersEverOpened(true);
    }
  }, [onFostersTab]);
  const {
    devices: drainedFoster,
    totalCount: drainedFosterTotal,
    complete: drainedFosterComplete,
    error: drainedFosterError,
    refetch: refetchFoster,
  } = useAllRescueDevices('IN_FOSTER', searchQuery, resellerName, { skip: !fostersEverOpened });
  // Left live on every tab, unlike the drain: it is a single row, and it means the badge and
  // the section header read one number that never blinks to zero while the drain restarts on
  // arriving at the tab.
  const fosterCount = useRescueDevices('IN_FOSTER', searchQuery, 1, resellerName);

  const sortedActivated = useMemo(
    () => sortDevices(drainedActivated, activatedSort),
    [drainedActivated, activatedSort],
  );
  const sortedFoster = useMemo(() => sortDevices(drainedFoster, fosterSort), [drainedFoster, fosterSort]);

  const activatedPagination = useClientPagination(sortedActivated, PAGE_SIZE);
  const fosterPagination = useClientPagination(sortedFoster, PAGE_SIZE);

  // The section shows a real total and count even mid-drain (from the server's
  // reported total), so switch the tab count to the drained total when sorting.
  const activatedTotalCount = activatedSortActive ? drainedActivatedTotal : rescue.totalCount;
  // Once the drain is complete its own total is the one that matches the rows on screen — it
  // was counted by the same response the rows came from, so the header can never read a total
  // the table contradicts. Ahead of that — mid-drain, or on another tab — the count query is
  // the only source there is, and it never blinks to zero the way the restarting drain would.
  const fosterTotalCount = drainedFosterComplete ? drainedFosterTotal : fosterCount.totalCount;

  // ── Pending adoptions (fetch-all, then split by age) ──
  //
  // We fetch every pending adoption up front and partition it client-side into
  // recent transfers ("Pending Transfers") and those awaiting activation for
  // more than STALLED_TRANSFER_DAYS ("Stalled Transfers"). Each section then
  // paginates client-side so its counts stay accurate.
  const {
    devices: allPending,
    loading: pendingLoading,
    error: pendingError,
  } = useAllPendingAdoptions(searchQuery, resellerName);

  const { freshTransfers, stalledTransfers } = useMemo(() => {
    const fresh: Device[] = [];
    const stalled: Device[] = [];
    for (const d of allPending) {
      // A transfer is stalled if the rescue explicitly flagged it (stalledAt) or
      // it has aged past the automatic threshold.
      if (d.stalledAt || isTransferStalled(d.transferDate, STALLED_TRANSFER_DAYS)) {
        stalled.push(d);
      } else {
        fresh.push(d);
      }
    }
    return { freshTransfers: fresh, stalledTransfers: stalled };
  }, [allPending]);

  // Stalled section is sortable by clicking the Adopter / Pet / Stalled headers.
  // Sort the full list before paginating so it spans all pages, not just the
  // current one. Default: most-stalled (oldest transfer) first.
  const [stalledSort, setStalledSort] = useState<{ key: StalledSortKey; dir: SortDir }>({
    key: 'stalled',
    dir: 'desc',
  });

  const sortedStalled = useMemo(() => {
    const arr = [...stalledTransfers];
    const { key, dir } = stalledSort;
    const mult = dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let cmp: number;
      if (key === 'adopter') {
        cmp = (a.adopterName ?? '').localeCompare(b.adopterName ?? '');
      } else if (key === 'pet') {
        cmp = (a.petName ?? '').localeCompare(b.petName ?? '');
      } else {
        // "Stalled" = age since transfer. Older transfer → more stalled, so a
        // larger age sorts as "greater". age ∝ -transferDate, hence tb - ta.
        const ta = a.transferDate ? new Date(a.transferDate).getTime() : Number.POSITIVE_INFINITY;
        const tb = b.transferDate ? new Date(b.transferDate).getTime() : Number.POSITIVE_INFINITY;
        cmp = tb - ta;
      }
      return cmp * mult;
    });
    return arr;
  }, [stalledTransfers, stalledSort]);

  // Pending section is sortable by clicking the Adopter / Pet / Device /
  // Transferred headers. Like Stalled, the full list is already in memory, so we
  // sort it before paginating (spans all pages). `pendingSort` is null until the
  // user clicks a header, preserving the default newest-first server order.
  const [pendingSort, setPendingSort] = useState<{ key: PendingSortKey; dir: SortDir } | null>(null);

  const sortedPending = useMemo(() => {
    if (!pendingSort) {
      return freshTransfers;
    }
    const arr = [...freshTransfers];
    const { key, dir } = pendingSort;
    const mult = dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let cmp: number;
      if (key === 'adopter') {
        cmp = (a.adopterName ?? '').localeCompare(b.adopterName ?? '');
      } else if (key === 'pet') {
        cmp = (a.petName ?? '').localeCompare(b.petName ?? '');
      } else if (key === 'device') {
        cmp = (a.id ?? '').localeCompare(b.id ?? '');
      } else {
        // "Transferred" = transfer date. Ascending = oldest first; undated rows
        // sort last. Missing dates → +Infinity so they trail in ascending order.
        const ta = a.transferDate ? new Date(a.transferDate).getTime() : Number.POSITIVE_INFINITY;
        const tb = b.transferDate ? new Date(b.transferDate).getTime() : Number.POSITIVE_INFINITY;
        cmp = ta - tb;
      }
      return cmp * mult;
    });
    return arr;
  }, [freshTransfers, pendingSort]);

  const pendingPagination = useClientPagination(sortedPending, PAGE_SIZE);
  const stalledPagination = useClientPagination(sortedStalled, PAGE_SIZE);

  // Jump both client-paginated sections back to page 1 whenever the search
  // changes. useClientPagination only clamps when the list shrinks, so without
  // this a user on page 3 would stay there and see a mid-list slice of the new
  // results instead of the top matches. (The server-paginated sections reset
  // via useRescueDevices.)
  const pendingResetPage = pendingPagination.resetPage;
  const stalledResetPage = stalledPagination.resetPage;
  useEffect(() => {
    pendingResetPage();
    stalledResetPage();
  }, [searchQuery, pendingResetPage, stalledResetPage]);

  // Toggle direction when re-clicking the active column; otherwise start a new
  // column ascending. Jump back to page 1 so the new top row is visible.
  function handleStalledSort(key: StalledSortKey) {
    setStalledSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
    stalledPagination.resetPage();
  }

  // First click on a column starts it ascending; re-clicking the active column
  // toggles direction. Jump back to page 1 so the new top row is visible.
  function handlePendingSort(key: PendingSortKey) {
    setPendingSort((prev) =>
      prev && prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
    pendingPagination.resetPage();
  }

  // First click on a column starts it ascending (and flips the section into the
  // drained/client-sorted mode); re-clicking the active column toggles
  // direction. Jump back to page 1 so the new top row is visible.
  function handleActivatedSort(key: DeviceSortKey) {
    setActivatedSort((prev) =>
      prev && prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
    activatedPagination.resetPage();
  }

  function handleFosterSort(key: DeviceSortKey) {
    setFosterSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      // Dates open on newest first (the question is "what moved recently"); the
      // name/ID columns open A–Z, matching the Activated section.
      return { key, dir: key === 'placed' ? 'desc' : 'asc' };
    });
    fosterPagination.resetPage();
  }

  // Reset the drained views to page 1 when the search changes (only relevant
  // once sorting is active; useClientPagination otherwise only clamps on
  // shrink). Mirrors the pending/stalled reset above.
  const activatedResetPage = activatedPagination.resetPage;
  const fosterResetPage = fosterPagination.resetPage;
  useEffect(() => {
    activatedResetPage();
    fosterResetPage();
  }, [searchQuery, activatedResetPage, fosterResetPage]);

  // Show skeletons for the whole Activated section until the drain is *fully*
  // complete — a partial list can't be sorted/paginated as if final (the top
  // rows would reshuffle as later pages arrive). Suppressed on error so the
  // error+retry row shows instead.
  const activatedSkeletonCount =
    activatedSortActive && !drainedActivatedComplete && !drainedActivatedError ? PAGE_SIZE : 0;
  const fosterSkeletonCount = !drainedFosterComplete && !drainedFosterError ? PAGE_SIZE : 0;

  // Show skeleton rows only on the very first load (no pending data yet).
  const pendingSkeletonCount = pendingLoading && allPending.length === 0 ? PAGE_SIZE : 0;

  // Pet IDs of fully-adopted-out pets — used to hide mutation actions on their
  // device rows. While loading, treat rescue-row pets as potentially adopted so
  // we don't briefly show buttons we're about to hide.
  const { adoptedPetIds, loading: adoptedLoading } = useAdoptedPetIds();

  // ── Unassigned devices ──
  //
  // The query is named `rescueUnassignedDeviceInventory` and only returns
  // devices not attached to a pet, so no client-side filter is needed.

  const unassignedDevices = useMemo(() => inventoryData?.rescueUnassignedDeviceInventory?.items ?? [], [inventoryData]);
  const unassignedCount = unassignedDevices.length;

  // Stock paginates client-side over the already-drained inventory (mirrors the
  // Pending/Stalled sections), and jumps back to page 1 when the search changes.
  const stockPagination = useClientPagination(unassignedDevices, PAGE_SIZE);
  const stockResetPage = stockPagination.resetPage;
  useEffect(() => {
    stockResetPage();
  }, [searchQuery, stockResetPage]);

  // ── Loading / Error states ────────────────

  // Only show the full-page loading spinner on initial page load (no search active).
  // During search, Apollo may briefly have empty data for new variables — we don't
  // want to unmount the search bar (which would steal focus from the input).
  const initialLoading =
    !searchQuery &&
    rescue.loading &&
    fosterCount.loading &&
    pendingLoading &&
    rescue.devices.length === 0 &&
    fosterCount.devices.length === 0 &&
    allPending.length === 0;
  const hasError = rescue.error && fosterCount.error && pendingError && inventoryError;

  // ── T2A Stats ──────────────────────────

  const pendingCount = stats?.pendingAdoptionsCount ?? 0;
  const completedCount = stats?.completedAdoptionsCount ?? 0;
  const totalTransfers = pendingCount + completedCount;
  const showStats = totalTransfers > 0;

  const allTimeRate = totalTransfers > 0 ? Math.round((completedCount / totalTransfers) * 100) : 0;

  function rateColorClass(pct: number): string {
    if (pct >= 80) {
      return styles.summaryValueGreen;
    }
    if (pct >= 60) {
      return styles.summaryValueAmber;
    }
    return styles.summaryValueRed;
  }

  // ── GraphQL mutations ─────────────────────

  // Every one of these refetches the active queries so the summary cards and the paginated
  // sections refresh. Which of the two drained lists each one spares — and why — lives with
  // the options in ./drainedMutationOptions; transferring to a foster is the only one that
  // spares neither, because the row it adds to the foster list has to come from the server.
  const [createFosterTransferMut] = useMutation(rescueCreatePetFosterTransferMutation, refetchAllOpts);
  // Spares neither drain, for the same reason the foster transfer above doesn't: the booking shows as
  // a badge on a row that is already in a drained list, and that field has to come from the server.
  const [scheduleHandoffMut] = useMutation(rescueScheduleHandoffMutation, refetchAllOpts);
  const [recallFosterTransfer] = useMutation(rescueRecallPetFosterTransferMutation, fosterRecallOpts);
  const [unassignDevice] = useMutation(unassignDeviceMutation, drainNeutralOpts);
  const [createFosterHome] = useMutation<RescueCreateFosterHomeData>(rescueCreateFosterHomeMutation, drainNeutralOpts);
  const [archiveFosterHome] = useMutation(rescueArchiveFosterHomeMutation, drainNeutralOpts);
  // These resolve a pending adoption transfer, so they maintain the drained
  // PENDING_ADOPTIONS cache directly rather than refetch-collapsing it.
  const [cancelAdoptionTransferMut] = useMutation(rescueCancelPetAdoptionTransferMutation, pendingResolveOpts);
  const [markNotRecoveredMut] = useMutation(rescueMarkAdoptionTransferNotRecoveredMutation, pendingResolveOpts);
  const [markRecoveredMut] = useMutation(rescueMarkAdoptionTransferRecoveredMutation, pendingResolveOpts);
  // Flagging as stalled keeps the transfer PENDING; the mutation returns the
  // updated transfer (id + stalledAt), which Apollo writes back to the same
  // normalized entity in the PENDING_ADOPTIONS drain. That re-runs the
  // fresh/stalled split so the row moves to Stalled — no refetch needed (a
  // refetch would collapse the drain, like the resolve mutations above).
  const [markStalledMut] = useMutation(rescueMarkAdoptionTransferStalledMutation);
  const [sendSmsMut] = useMutation(rescueSendSmsMutation);
  const transferPetWithChip = useTransferPetWithChipToAdopter(adoptOutOpts);

  /** Reset all sections to page 1 after a mutation. */
  function resetAllPages() {
    rescue.resetPage();
    activatedPagination.resetPage();
    fosterPagination.resetPage();
    pendingPagination.resetPage();
    stalledPagination.resetPage();
    completed.resetPage();
  }

  // ── Helper: find device across all sections ──

  function findDevice(deviceId: string): Device | undefined {
    return (
      rescue.devices.find((d) => d.id === deviceId) ??
      // A section renders from its drained list while sorting (and the matching
      // server query is skipped), so consult those too — otherwise row actions on a
      // sorted page beyond the first would silently no-op. `drainedFoster` is always
      // populated; `drainedActivated` is empty until the Activated section is sorted.
      drainedActivated.find((d) => d.id === deviceId) ??
      drainedFoster.find((d) => d.id === deviceId) ??
      allPending.find((d) => d.id === deviceId) ??
      completed.devices.find((d) => d.id === deviceId)
    );
  }

  // ── Modal state ──────────────────────────

  const [addPetOpen, setAddPetOpen] = useState(false);
  const [addPetDevice, setAddPetDevice] = useState<Device | null>(null);
  const [fosterOpen, setFosterOpen] = useState(false);
  const [fosterDevice, setFosterDevice] = useState<Device | null>(null);
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallDevice, setRecallDevice] = useState<Device | null>(null);
  const [adopterOpen, setAdopterOpen] = useState(false);
  const [adopterDevice, setAdopterDevice] = useState<Device | null>(null);
  const [adoptFromFosterOpen, setAdoptFromFosterOpen] = useState(false);
  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [scheduledFocusId, setScheduledFocusId] = useState<string | null>(null);
  const [scheduledFocusAction, setScheduledFocusAction] = useState<'reschedule' | 'cancel'>('reschedule');
  const [adoptFromFosterDevice, setAdoptFromFosterDevice] = useState<Device | null>(null);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendDevice, setResendDevice] = useState<Device | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelDevice, setCancelDevice] = useState<Device | null>(null);
  // When the cancel flow is opened from the Stalled table the transfer is
  // already stalled, so we hide the "move to Stalled" reason (see below).
  const [cancelHideStalledOption, setCancelHideStalledOption] = useState(false);
  const [reenterOpen, setReenterOpen] = useState(false);
  const [reenterDevice, setReenterDevice] = useState<Device | null>(null);
  const [returnedOpen, setReturnedOpen] = useState(false);
  const [returnedDevice, setReturnedDevice] = useState<Device | null>(null);
  const [keptOpen, setKeptOpen] = useState(false);
  const [keptDevice, setKeptDevice] = useState<Device | null>(null);
  const [testingOpen, setTestingOpen] = useState(false);
  const [testingDevice, setTestingDevice] = useState<Device | null>(null);
  const [petDeviceReturnedOpen, setPetDeviceReturnedOpen] = useState(false);
  const [petDeviceReturnedDevice, setPetDeviceReturnedDevice] = useState<Device | null>(null);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsDevice, setSmsDevice] = useState<Device | null>(null);
  const [markRecoveredOpen, setMarkRecoveredOpen] = useState(false);
  const [markRecoveredDevice, setMarkRecoveredDevice] = useState<Device | null>(null);
  const [notRecoveredOpen, setNotRecoveredOpen] = useState(false);
  const [notRecoveredDevice, setNotRecoveredDevice] = useState<Device | null>(null);
  const [assignPetOpen, setAssignPetOpen] = useState(false);
  const [assignPetModuleId, setAssignPetModuleId] = useState<string | null>(null);
  const [orderDevicesOpen, setOrderDevicesOpen] = useState(false);
  // Modal opens by default for users who haven't enrolled in the devices
  // program. Once dismissed it stays closed for the rest of the page lifetime;
  // the empty state's CTA re-opens it.
  const [enrollModalDismissed, setEnrollModalDismissed] = useState(false);
  const enrollModalOpen = !enrollmentPending && !isEnrolled && !enrollModalDismissed;
  // ── Modal handlers ──────────────────────────

  function handleOpenAddPet(device: Device) {
    setAddPetDevice(device);
    setAddPetOpen(true);
  }

  function handleOpenFoster(device: Device) {
    setFosterDevice(device);
    setFosterOpen(true);
  }

  function handleOpenRecall(device: Device) {
    setRecallDevice(device);
    setRecallOpen(true);
  }

  function handleOpenAdopter(device: Device) {
    setAdopterDevice(device);
    setAdopterOpen(true);
  }

  function handleOpenAdoptFromFoster(device: Device) {
    setAdoptFromFosterDevice(device);
    setAdoptFromFosterOpen(true);
  }

  /** Reuse-first: an existing contact comes back refreshed, which the modal has already warned about. */
  async function handleCreateFosterHome(input: RescueCreateFosterHomeInput) {
    // Captured before the write, since the mutation refetches active queries and the roster is one.
    const knownIds = new Set(fosterHomes.homes.map((home) => home.id));
    const { data } = await createFosterHome({ variables: { input } });

    // Only a genuinely new record is a creation. The mutation returns the home the contact mapped
    // to, so an id already on the roster means it refreshed one rather than added one — counting
    // that would inflate the roster's growth with edits. Safe to judge from the roster here: this
    // handler is only reachable from the Fosters tab, which has it loaded to render the table.
    const saved = data?.rescueCreateFosterHome ?? null;
    if (saved && !knownIds.has(saved.id)) {
      trackFosterHomeCreated();
    }

    showToast(`${input.contactFirstName} saved to your foster roster`);
  }

  /** Rejected by the backend if a pet is still placed. */
  async function handleArchiveFosterHome(home: FosterHome) {
    await archiveFosterHome({ variables: { id: home.id } });
    trackFosterHomeArchived(home.placementCount);
    showToast(`${home.fullName} archived`);
  }

  function handleOpenResend(device: Device) {
    setResendDevice(device);
    setResendOpen(true);
  }

  // `hideStalled` is set when opening from the Stalled table, where the transfer
  // is already stalled and the "move to Stalled" reason would be a no-op.
  function handleOpenCancel(device: Device, hideStalled = false) {
    setCancelDevice(device);
    setCancelHideStalledOption(hideStalled);
    setCancelOpen(true);
  }

  function handleOpenSms(device: Device) {
    setSmsDevice(device);
    setSmsOpen(true);
  }

  function handleOpenAssignPet(moduleId: string) {
    setAssignPetModuleId(moduleId);
    setAssignPetOpen(true);
  }

  // ── Mutation handlers ──────────────────────────

  function handleAddPet(_deviceId: string, petName: string) {
    void petName;
    showToast(`Pet name update is not yet supported via the API`);
  }

  // Named `transfer`, not `foster`: this page uses `foster*` for the IN_FOSTER device
  // section's own state, so a bare `foster` here would read as one of those.
  async function handleTransferToFoster(deviceId: string, transfer: FosterTransferInput) {
    const { firstName, lastName, email: fosterEmail, phone: fosterPhone, saveToRoster } = transfer;
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    // Straight from one foster home to the next: the pet has to come back to the rescue in between.
    if (device.holderType === 'foster' && !(await recallBeforeHandoff(device))) {
      return;
    }

    try {
      await createFosterTransferMut({
        variables: {
          input: {
            petId: device._petGraphqlId,
            email: fosterEmail,
            firstName,
            lastName: lastName || undefined,
            phoneNumber: fosterPhone,
            // false leaves the home off the roster, so recall tears it down.
            saveToRoster,
          },
        },
      });
      trackDeviceTransferToFoster('devices_page', { fosterSource: transfer.source, saveToRoster });
      resetAllPages();
      showToast(`${device.id} assigned to foster`);
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleRecall(deviceId: string, saveToRoster: boolean) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    if (device.holderType === 'foster') {
      // Foster recall — revoke the foster transfer
      try {
        await recallFosterTransfer({
          variables: {
            // true keeps the foster's household on the roster instead of tearing it down.
            input: { petId: device._petGraphqlId, saveToRoster },
          },
        });
        trackDeviceRecalled('devices_page', { saveToRoster });
        resetAllPages();
        showToast(`${device.id} recalled to rescue`);
      } catch (err) {
        showToast(`Failed to recall: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    } else {
      // Non-foster — just unassign the device from the pet
      try {
        await unassignDevice({
          variables: {
            moduleId: device.id,
            petId: device._petGraphqlId,
          },
        });
        resetAllPages();
        showToast(`Pet removed from ${device.id}`);
      } catch (err) {
        showToast(`Failed to remove pet: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }
  }

  async function runAdopterTransfer(
    device: Device,
    firstName: string,
    lastName: string,
    adopterEmail: string,
    adopterPhone: string,
    source: string,
    // True on the foster→adopt path, where the foster was already recalled
    // before this ran. If the transfer then fails, the pet is left in the rescue
    // household (no longer fostered), so the failure message must say so and the
    // view must refresh to reflect the recall.
    fosterRecalled = false,
  ) {
    let result;
    try {
      result = await transferPetWithChip({
        petId: device._petGraphqlId,
        chipId: device.chip?.id ?? null,
        adopter: {
          firstName,
          lastName,
          email: adopterEmail,
          phone: adopterPhone,
          street: '',
          apt: '',
          city: '',
          state: '',
          zip: '',
        },
      });
      trackDeviceTransferToAdopt(source);
      resetAllPages();
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Unknown error';
      if (fosterRecalled) {
        resetAllPages();
        showToast(
          `Foster recalled, but the adoption failed for ${device.id}: ${detail}. ${
            device.petName || 'The pet'
          } is now in your rescue's devices — retry from there.`,
        );
      } else {
        showToast(`Failed to transfer: ${detail}`);
      }
      return;
    }

    if (result.chipAssigned) {
      trackChipOwnerAdded('devices_t2a');
      showToast(`Transfer initiated for ${device.id}; chip ownership updated`);
    } else if (result.chipError) {
      const detail = result.chipError instanceof Error ? result.chipError.message : 'Unknown error';
      showToast(`Transfer initiated for ${device.id}, but chip transfer failed: ${detail}`);
    } else {
      showToast(`Transfer initiated for ${device.id}`);
    }
  }

  async function handleTransferToAdopter(
    firstName: string,
    lastName: string,
    adopterEmail: string,
    adopterPhone: string,
  ) {
    const device = adopterDevice;
    if (!device) {
      return;
    }
    const fromFoster = device.holderType === 'foster';
    if (fromFoster && !(await recallBeforeHandoff(device))) {
      return;
    }
    await runAdopterTransfer(device, firstName, lastName, adopterEmail, adopterPhone, 'devices_page', fromFoster);
  }

  // One-click adopt from the Fosters table: adopt the pet out to the foster
  // household using their existing contact info, with no modal.
  //
  // The foster placement MUST be recalled before creating the adoption transfer.
  // createPetAdoptionTransfer moves the pet out of whatever household it's in and
  // records that as the transfer's `fromHousehold`; run directly on a fostered pet
  // it would set the foster household as `fromHousehold`, strand the still-ACTIVE
  // foster transfer, and leave the foster household abandoned — so a later
  // cancel/recover would send the pet back to the foster, not the rescue. Recall
  // first returns the pet to the rescue household (ending the foster transfer),
  // which is the clean, rescue-held state the adopter transfer expects.
  /**
   * Books any of the three hand-offs. Errors are rethrown so the modal can keep itself open and show
   * them on the schedule step, rather than closing over a booking that never landed.
   */
  /**
   * Takes the pet back from its foster so a hand-off can run from the rescue's household. Used by the
   * Fosters-tab actions that move a pet straight to another foster or to an adopter. Returns false
   * when the recall failed, in which case the caller must not go on to the hand-off.
   *
   * `saveToRoster: false` matches the recall modal's unticked default and the foster-fail path.
   */
  async function recallBeforeHandoff(device: Device) {
    try {
      await recallFosterTransfer({ variables: { input: { petId: device._petGraphqlId, saveToRoster: false } } });
      trackDeviceRecalled('devices_page', { saveToRoster: false });
      return true;
    } catch (err) {
      showToast(`Failed to recall: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return false;
    }
  }

  /** Opens this pet's booking on the step the rescue asked for — its date and time, or calling it off. */
  function openScheduled(device: Device, action: 'reschedule' | 'cancel') {
    const handoffId = device.scheduledHandoff?.id;
    if (!handoffId) {
      return;
    }
    setScheduledFocusId(handoffId);
    setScheduledFocusAction(action);
    setScheduledOpen(true);
  }

  const handleEditScheduled = (device: Device) => openScheduled(device, 'reschedule');
  const handleCancelScheduled = (device: Device) => openScheduled(device, 'cancel');

  async function scheduleHandoff(
    device: Device,
    kind: 'ADOPTION' | 'FOSTER' | 'ADOPTION_FROM_FOSTER',
    when: ScheduledFor,
    contact?: { firstName: string; lastName?: string; email?: string; phone?: string; saveToRoster?: boolean },
    entryPoint?: string,
  ) {
    await scheduleHandoffMut({
      variables: {
        input: {
          petId: device._petGraphqlId,
          kind,
          ...when,
          firstName: contact?.firstName,
          lastName: contact?.lastName || undefined,
          email: contact?.email || undefined,
          phoneNumber: contact?.phone || undefined,
          saveToRoster: contact?.saveToRoster,
          // Booked from the Fosters tab, so the recall is part of the booking rather than something
          // the rescue has to remember to do at the scheduled time.
          recallFirst: device.holderType === 'foster',
          // The chip moves with the adoption; the server does it after the transfer lands.
          chipId: kind === 'FOSTER' ? undefined : (device.chip?.serialNumber ?? undefined),
        },
      },
    });

    const hoursOut = Math.round((new Date(when.scheduledFor).getTime() - Date.now()) / 3_600_000);
    trackHandoffScheduled(entryPoint, { kind, hoursOut });
    showToast(`Scheduled for ${formatScheduledFor(when.scheduledFor, when.scheduledForTimezone)}`);
  }

  async function handleAdoptFromFoster(device: Device) {
    if (!isFosterContactComplete(device)) {
      showToast(`Foster contact info is incomplete — cannot start adoption`);
      return;
    }
    // Capture the foster's contact info as the adopter details. `isFosterContactComplete`
    // guarantees these are set; the `?? ''` fallbacks just satisfy the type checker
    // (property narrowing is dropped across the awaited recall below).
    const firstName = device.holderFirstName ?? '';
    const lastName = device.holderLastName ?? '';
    const email = device.holderEmail ?? '';
    const phone = device.holderPhone ?? '';

    try {
      await recallFosterTransfer({
        // Sent explicitly rather than omitted: the field defaults to true on the transfer path, so
        // leaving it out would let the request and the event below disagree with nothing to notice it.
        variables: { input: { petId: device._petGraphqlId, saveToRoster: false } },
      });
      // This flow recalls without the option, so the home survives only if it was already saved.
      trackDeviceRecalled('devices_foster_adopt', { saveToRoster: false });
    } catch (err) {
      showToast(`Failed to start adoption: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return;
    }

    await runAdopterTransfer(device, firstName, lastName, email, phone, 'devices_foster_adopt', true);
  }

  // Each cancel reason resolves the pending adoption differently, so rather than
  // acting immediately we route to a per-reason confirmation modal that spells
  // out the outcome and performs the mutation once confirmed.
  function handleCancelAdoptionTransfer(device: Device, reason: CancellationReason) {
    switch (reason) {
      case 'wrong_adopter_info':
        // Cancel the transfer, then reopen the adopter screen prefilled.
        setReenterDevice(device);
        setReenterOpen(true);
        return;
      case 'device_returned':
        // Recover the device back to the rescue's unassigned stock.
        setReturnedDevice(device);
        setReturnedOpen(true);
        return;
      case 'device_kept':
        // Mark not recovered so it stays tracked in the Stalled table.
        setKeptDevice(device);
        setKeptOpen(true);
        return;
      case 'pet_and_device_returned':
        // Adoption fell through — plain cancel returns the device to inventory
        // and the pet to the rescue (same action as testing, its own trace).
        setPetDeviceReturnedDevice(device);
        setPetDeviceReturnedOpen(true);
        return;
      case 'testing':
        // No real adoption — plain cancel returns the device to inventory.
        setTestingDevice(device);
        setTestingOpen(true);
        return;
    }
  }

  async function handleConfirmTestingCancel(device: Device) {
    try {
      // No real adoption — plain cancel returns the device to inventory.
      await cancelAdoptionTransferMut({ variables: { input: { petId: device._petGraphqlId } } });
      trackDeviceCancelTransfer('devices_page', 'testing');
      resetAllPages();
      showToast(`Adoption transfer cancelled for ${device.id}`);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'testing');
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so TestingCancelModal stays open for retry.
      throw err;
    }
  }

  async function handleConfirmPetAndDeviceReturned(device: Device) {
    try {
      // Adopter brought both the pet and the collar back. A plain cancel pulls the
      // pet back into the rescue household with the collar still attached (unlike
      // recover, which unassigns the collar to bare stock). Same mutation as the
      // testing flow — it only differs in the analytics reason recorded below.
      await cancelAdoptionTransferMut({ variables: { input: { petId: device._petGraphqlId } } });
      trackDeviceCancelTransfer('devices_page', 'pet_and_device_returned');
      resetAllPages();
      showToast(`Adoption cancelled for ${device.petName ?? device.id} — back in your rescue with its collar`);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'pet_and_device_returned');
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so PetAndDeviceReturnedCancelModal stays open for retry.
      throw err;
    }
  }

  async function handleConfirmDeviceKept(device: Device) {
    try {
      // Adopter kept the collar and the rescue doesn't have it (yet). Flag the
      // still-PENDING transfer as stalled so it moves to the Stalled Transfers
      // table for follow-up, rather than resolving it now.
      await markStalledMut({ variables: { input: { petId: device._petGraphqlId } } });
      // Reason event captures the cancel-flow intent; the dedicated stalled event
      // captures the actual state transition (consistent with recovered / not-recovered).
      trackDeviceCancelTransfer('devices_page', 'device_kept');
      trackDeviceMarkedStalled('devices_page');
      resetAllPages();
      showToast(`${device.petName ?? device.id} moved to Stalled Transfers`);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'device_kept');
      showToast(`Failed to flag as stalled: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so DeviceKeptModal stays open for retry.
      throw err;
    }
  }

  async function handleConfirmDeviceReturned(device: Device) {
    try {
      // Adopter keeps the pet but handed the collar back — recover it so it
      // returns to the rescue's unassigned stock.
      await markRecoveredMut({ variables: { input: { petId: device._petGraphqlId } } });
      // Reason event captures the cancel-flow intent; the dedicated recovered event
      // keeps recovery counts consistent with the standalone Mark-Recovered flow
      // (same mutation, same signal).
      trackDeviceCancelTransfer('devices_page', 'device_returned');
      trackDeviceMarkedRecovered('devices_page');
      resetAllPages();
      showToast(`${device.id} recovered — back in your stock`);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'device_returned');
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so DeviceReturnedModal stays open for retry.
      throw err;
    }
  }

  async function handleConfirmReenterAdopter(device: Device) {
    const petId = device._petGraphqlId;
    try {
      // Bad adopter details — cancel the transfer (pet is kept) so we can reopen
      // the adoption screen prefilled and re-enter the correct info.
      await cancelAdoptionTransferMut({ variables: { input: { petId } } });
      trackDeviceCancelTransfer('devices_page', 'wrong_adopter_info');
      resetAllPages();
      showToast('Transfer cancelled — update the adopter details and resend');
      // Reopen the adopter transfer screen so the user can correct the info.
      handleOpenAdopter(device);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'wrong_adopter_info');
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so WrongAdopterInfoModal stays open for retry.
      throw err;
    }
  }

  // ── Stalled transfer actions ──
  //
  // Send SMS reuses the existing SMS modal (handleOpenSms).

  function handleMarkRecovered(device: Device) {
    setMarkRecoveredDevice(device);
    setMarkRecoveredOpen(true);
  }

  async function handleConfirmMarkRecovered(device: Device) {
    try {
      // Closes the transfer and unassigns the device back to the rescue's stock,
      // recording a dedicated "recovered" audit event server-side.
      await markRecoveredMut({
        variables: { input: { petId: device._petGraphqlId } },
      });
      trackDeviceMarkedRecovered('devices_page');
      resetAllPages();
      showToast(`${device.id} recovered — back in your stock`);
    } catch (err) {
      showToast(`Failed to recover device: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so MarkRecoveredModal stays open for retry.
      throw err;
    }
  }

  function handleNotRecovered(device: Device) {
    setNotRecoveredDevice(device);
    setNotRecoveredOpen(true);
  }

  async function handleConfirmNotRecovered(device: Device) {
    try {
      // Steps the rescue out of the adoption household and marks the transfer
      // NOT_RECOVERED. The collar stays attached to its pet (so it doesn't
      // return to rescue stock) but is left claimable by a future owner.
      await markNotRecoveredMut({
        variables: { input: { petId: device._petGraphqlId } },
      });
      trackDeviceMarkedNotRecovered('devices_page');
      resetAllPages();
      showToast(`${device.id} marked not recovered`);
    } catch (err) {
      showToast(`Failed to mark not recovered: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so NotRecoveredModal stays open for retry.
      throw err;
    }
  }

  async function handleSendSms(phoneNumber: string, message: string) {
    try {
      await sendSmsMut({ variables: { input: { phoneNumber, message } } });
      trackDeviceSmsSent();
      showToast('SMS sent successfully');
    } catch (err) {
      showToast(`Failed to send SMS: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleResendEmail(deviceId: string) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    const to = device.adopterEmail ?? '';
    const subject = encodeURIComponent(`Set up your Fi collar${device.petName ? ` for ${device.petName}` : ''}`);
    const body = encodeURIComponent(
      `Hi${device.adopterName ? ` ${device.adopterName}` : ''},\n\nCongratulations on your adoption${
        device.petName ? ` of ${device.petName}` : ''
      }! Your new companion comes with a Fi GPS collar (${
        device.id
      }).\n\nTo activate the collar and start tracking, download the Fi app and follow the setup instructions:\nhttps://fitracking.com/app\n\nIf you have any questions, feel free to reach out to us or the Fi team at support@fitracking.com.\n\nBest,\n${orgName}`,
    );
    trackDeviceResendEmail();
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  }

  // ── Order Devices ──────────────────────────

  function handleOrderDevices() {
    trackDevicesOrderClicked(enrollmentData?.rescueDevicesProgram.deviceOrderAttestation.variant);
    setOrderDevicesOpen(true);
  }

  // ── Render ──────────────────────────

  if (enrollmentPending) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <LoadingSpinner />
      </>
    );
  }

  if (!isEnrolled) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <EnrollEmptyState
          description="Not enrolled yet. Add GPS tracking to your rescue program to track fosters and reward adopters."
          ctaLabel="Set up now"
          onCta={() => setEnrollModalDismissed(false)}
        />
        <DevicesEnrollModal
          open={enrollModalOpen}
          onClose={() => setEnrollModalDismissed(true)}
          onEnrolled={() => {
            setEnrollModalDismissed(true);
            refetchEnrollment();
          }}
        />
      </>
    );
  }

  // Don't blank the page out from under an open order form. OrderDevicesModal is reachable from the
  // page header, so unlike every other modal here it can be open while the device queries reload —
  // and this branch would unmount it, destroying a part-filled order or the confirmation for one
  // just placed. Falling through renders the same empty state an enrolled rescue with no devices
  // already sees, behind the modal.
  if (initialLoading && !orderDevicesOpen) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <LoadingSpinner message="Loading devices..." />
      </>
    );
  }

  if (hasError) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <div className={styles.errorContainer}>
          <p>Failed to load devices. Please try again.</p>
        </div>
      </>
    );
  }

  // ── Tabs & device sub-filter ──────────────
  const tabs: TabDef[] = [
    { key: 'devices', label: 'Devices', count: unassignedCount + activatedTotalCount },
    { key: 'fosters', label: 'Fosters', count: fosterTotalCount },
    { key: 'adoptions', label: 'Adoptions', count: allPending.length + completed.totalCount },
  ];

  const showStock = deviceFilter === 'all' || deviceFilter === 'stock';
  const showActivated = deviceFilter === 'all' || deviceFilter === 'activated';
  const showInTransit = deviceFilter === 'all' || deviceFilter === 'inTransit';

  const adoptionsEmpty = freshTransfers.length === 0 && stalledTransfers.length === 0 && completed.totalCount === 0;

  return (
    <>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <h1>Fi Devices</h1>
        <div className={styles.headerActions}>
          {/* Only once something is booked: an entry point to an empty list is a dead control, and
              its presence is itself the signal that hand-offs are waiting. */}
          {scheduledCount > 0 && (
            <Button variant="ghost" onClick={() => setScheduledOpen(true)}>
              Scheduled · {scheduledCount}
            </Button>
          )}
          <Button
            variant="green"
            onClick={handleOrderDevices}
            disabled={!!pendingDeviceOrder}
            title={pendingDeviceOrder ? 'An order is awaiting approval' : undefined}
          >
            <svg
              className={styles.headerBtnIcon}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Order Devices
          </Button>
        </div>
      </div>

      {pendingDeviceOrder && (
        <div className={styles.pendingOrderBanner}>
          <svg
            className={styles.pendingOrderBannerIcon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            <path d="M12 7.5V12L15 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>{pendingDeviceOrder.message}</span>
        </div>
      )}

      {/* T2A Summary Stats */}
      {showStats && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Total Pending</div>
            <div className={`${styles.summaryCardValue} ${styles.summaryValueAmber}`}>{pendingCount}</div>
            <div className={styles.summaryCardSubtext}>awaiting adopter activation</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Transfer to Adoption Rate</div>
            <div className={`${styles.summaryCardValue} ${rateColorClass(allTimeRate)}`}>{allTimeRate}%</div>
            <div className={styles.summaryCardSubtext}>
              {completedCount} completed, {pendingCount} pending
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Total Fi Pets</div>
            <div className={`${styles.summaryCardValue} ${styles.summaryValueGreen}`}>{completedCount}</div>
            <div className={styles.summaryCardSubtext}>adopted through your rescue</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <PortalTabs tabs={tabs} activeTab={currentTab} onTabChange={handleTabChange} />

      {/* Search + device sub-filters (the filters apply to the Devices tab only) */}
      <div className={styles.searchFilterRow}>
        <div className={styles.searchFilterSearch}>
          <SearchBar
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search by device ID, pet name, or holder..."
          />
        </div>
        {currentTab === 'devices' && (
          <div className={styles.deviceFilters}>
            {DEVICE_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`${styles.filterChip} ${deviceFilter === f.key ? styles.filterChipActive : ''}`}
                onClick={() => setDeviceFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Devices tab — Activated, Stock, In Transit */}
      {currentTab === 'devices' && (
        <>
          {showActivated && (
            <DeviceSectionTable
              title="Activated"
              description="Devices attached to pets currently in your rescue's household."
              emptyLabel="activated devices"
              showHolder={false}
              // Default: server-paginated `rescue`. Once a column sort is active,
              // switch to the drained, client-sorted/paginated full list so the
              // sort spans every page rather than just the visible one. Rows are
              // only shown once the drain is complete (activatedSkeletonCount
              // covers the loading gap); a partial list is never displayed sorted.
              devices={
                activatedSortActive ? (drainedActivatedComplete ? activatedPagination.pageItems : []) : rescue.devices
              }
              totalCount={activatedTotalCount}
              currentPage={activatedSortActive ? activatedPagination.currentPage : rescue.currentPage}
              totalPages={activatedSortActive ? activatedPagination.totalPages : rescue.totalPages}
              pageSize={PAGE_SIZE}
              onPageChange={activatedSortActive ? activatedPagination.goToPage : rescue.goToPage}
              skeletonCount={activatedSortActive ? activatedSkeletonCount : rescue.skeletonCount}
              hasError={activatedSortActive && !!drainedActivatedError}
              onRetry={refetchActivated}
              searchQuery={searchQuery}
              sortKey={activatedSort?.key ?? null}
              sortDir={activatedSort?.dir}
              onSort={handleActivatedSort}
              adoptedPetIds={adoptedPetIds}
              adoptedLoading={adoptedLoading}
              onAddPet={handleOpenAddPet}
              onTransferFoster={handleOpenFoster}
              onRecall={handleOpenRecall}
              onTransferAdopter={handleOpenAdopter}
              onEditScheduled={handleEditScheduled}
              onCancelScheduled={handleCancelScheduled}
              onSendSms={handleOpenSms}
            />
          )}
          {showStock && (
            <StockTable
              devices={stockPagination.pageItems}
              totalCount={stockPagination.totalCount}
              currentPage={stockPagination.currentPage}
              totalPages={stockPagination.totalPages}
              pageSize={PAGE_SIZE}
              onPageChange={stockPagination.goToPage}
              loading={inventoryLoading}
              hasError={!!inventoryError}
              onRetry={() => {
                refetchInventory();
              }}
              onAssign={handleOpenAssignPet}
              searchQuery={searchQuery}
            />
          )}
          {showInTransit && (
            <DevicesInTransitTable
              shipments={shipments}
              pendingOrder={pendingDeviceOrder}
              showWhenEmpty={deviceFilter === 'inTransit'}
            />
          )}
        </>
      )}

      {/* Fosters tab — placements, then the saved-foster roster */}
      {currentTab === 'fosters' && (
        <>
          <DeviceSectionTable
            title="Devices with a Foster"
            description="Devices attached to pets currently placed with a foster."
            emptyLabel="foster devices"
            showPlacedOn
            // Always reads from the drained, client-sorted/paginated full list so the
            // sort spans every page rather than just the visible one. Rows show only
            // once the drain is complete — a partial list can't be sorted as if final.
            devices={drainedFosterComplete ? fosterPagination.pageItems : []}
            totalCount={fosterTotalCount}
            currentPage={fosterPagination.currentPage}
            totalPages={fosterPagination.totalPages}
            pageSize={PAGE_SIZE}
            onPageChange={fosterPagination.goToPage}
            skeletonCount={fosterSkeletonCount}
            hasError={!!drainedFosterError}
            onRetry={refetchFoster}
            searchQuery={searchQuery}
            sortKey={fosterSort.key}
            sortDir={fosterSort.dir}
            onSort={handleFosterSort}
            adoptedPetIds={adoptedPetIds}
            adoptedLoading={adoptedLoading}
            onAddPet={handleOpenAddPet}
            onTransferFoster={handleOpenFoster}
            onRecall={handleOpenRecall}
            onTransferAdopter={handleOpenAdopter}
            onAdoptFromFoster={handleOpenAdoptFromFoster}
            onEditScheduled={handleEditScheduled}
            onCancelScheduled={handleCancelScheduled}
            onViewFoster={roster.openDetail}
            fosterHomes={fosterHomes.homes}
            onSendSms={handleOpenSms}
          />
          <FosterRosterTable
            homes={fosterHomes.homes}
            loading={fosterHomes.loading}
            hasError={fosterHomes.error}
            onRetry={fosterHomes.refetch}
            pageSize={PAGE_SIZE}
            onView={roster.openDetail}
            onAdd={() => roster.setAddOpen(true)}
          />
        </>
      )}

      {/* Adoptions tab — Pending, Completed, Stalled */}
      {currentTab === 'adoptions' && (
        <>
          <PendingTransfersTable
            devices={pendingPagination.pageItems}
            totalCount={pendingPagination.totalCount}
            currentPage={pendingPagination.currentPage}
            totalPages={pendingPagination.totalPages}
            pageSize={PAGE_SIZE}
            onPageChange={pendingPagination.goToPage}
            loading={pendingLoading}
            skeletonCount={pendingSkeletonCount}
            sortKey={pendingSort?.key ?? null}
            sortDir={pendingSort?.dir}
            onSort={handlePendingSort}
            onResendEmail={handleOpenResend}
            onCancelTransfer={handleOpenCancel}
            onSendSms={handleOpenSms}
          />
          <CompletedTransfersTable
            devices={completed.devices}
            totalCount={completed.totalCount}
            currentPage={completed.currentPage}
            totalPages={completed.totalPages}
            pageSize={PAGE_SIZE}
            onPageChange={completed.goToPage}
            loading={completed.loading}
            skeletonCount={completed.skeletonCount}
          />
          <StalledTransfersTable
            devices={stalledPagination.pageItems}
            totalCount={stalledPagination.totalCount}
            currentPage={stalledPagination.currentPage}
            totalPages={stalledPagination.totalPages}
            pageSize={PAGE_SIZE}
            thresholdDays={STALLED_TRANSFER_DAYS}
            onPageChange={stalledPagination.goToPage}
            skeletonCount={pendingSkeletonCount}
            sortKey={stalledSort.key}
            sortDir={stalledSort.dir}
            onSort={handleStalledSort}
            onMarkRecovered={handleMarkRecovered}
            onNotRecovered={handleNotRecovered}
            onSendSms={handleOpenSms}
            onCancelTransfer={(device) => handleOpenCancel(device, true)}
          />
          {adoptionsEmpty && pendingSkeletonCount === 0 && completed.skeletonCount === 0 && (
            <div className={styles.tabEmptyState}>
              No adoptions to show{searchQuery ? ` for "${searchQuery}"` : ''}.
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <AddPetModal
        open={addPetOpen}
        onClose={() => setAddPetOpen(false)}
        device={addPetDevice}
        onAddPet={handleAddPet}
      />
      <TransferToFosterModal
        open={fosterOpen}
        onClose={() => setFosterOpen(false)}
        deviceId={fosterDevice?.id ?? null}
        petName={fosterDevice?.petName ?? null}
        homes={fosterHomes.homes}
        homesLoaded={fosterHomes.loaded}
        recallFirst={fosterDevice?.holderType === 'foster'}
        excludeHomeId={
          fosterDevice?.holderType === 'foster'
            ? (findFosterHomeForDevice(fosterDevice, fosterHomes.homes)?.id ?? null)
            : null
        }
        onTransfer={(transfer) => {
          if (fosterDevice) {
            handleTransferToFoster(fosterDevice.id, transfer);
          }
        }}
        onSchedule={async (transfer, when) => {
          if (fosterDevice) {
            await scheduleHandoff(fosterDevice, 'FOSTER', when, transfer, 'devices_page');
          }
        }}
      />
      <RecallDeviceModal
        open={recallOpen}
        onClose={() => setRecallOpen(false)}
        device={recallDevice}
        homes={fosterHomes.homes}
        homesLoaded={fosterHomes.loaded}
        onRecall={(saveToRoster) => {
          if (recallDevice) {
            handleRecall(recallDevice.id, saveToRoster);
          }
        }}
      />
      <AdoptFromFosterModal
        open={adoptFromFosterOpen}
        onClose={() => {
          setAdoptFromFosterOpen(false);
          setAdoptFromFosterDevice(null);
        }}
        device={adoptFromFosterDevice}
        homes={fosterHomes.homes}
        onConfirm={handleAdoptFromFoster}
        onSchedule={async (device, when) => {
          // The adopter is the current foster, so the server reads that contact off the open foster
          // transfer rather than taking it from here.
          await scheduleHandoff(device, 'ADOPTION_FROM_FOSTER', when, undefined, 'devices_foster_adopt');
        }}
      />

      <ScheduledHandoffsModal
        open={scheduledOpen}
        focusHandoffId={scheduledFocusId}
        focusAction={scheduledFocusAction}
        onClose={() => {
          setScheduledOpen(false);
          setScheduledFocusId(null);
        }}
      />

      <FosterHomeDetailsModal
        open={roster.detailOpen}
        onClose={roster.closeDetail}
        home={roster.detailHome}
        onArchive={handleArchiveFosterHome}
        viewablePlacementIds={roster.viewablePlacementIds}
        onViewPlacementPet={roster.viewPlacementPet}
      />

      {/* Read-only: pet edits belong on the Dashboard, which owns those flows. */}
      <ViewPetModal
        open={!!roster.placementPet}
        onClose={roster.closePlacementPet}
        pet={roster.placementPet ? { ...roster.placementPet, status: 'foster' } : null}
      />

      <AddFosterModal
        open={roster.addOpen}
        onClose={() => roster.setAddOpen(false)}
        onCreate={handleCreateFosterHome}
      />
      <TransferToAdopterModal
        open={adopterOpen}
        onClose={() => {
          setAdopterOpen(false);
          setAdopterDevice(null);
        }}
        deviceId={adopterDevice?.id ?? ''}
        petName={adopterDevice?.petName ?? null}
        chipSerialNumber={adopterDevice?.chip?.serialNumber ?? null}
        showRescueDiscount
        initialFirstName={adopterDevice?.adopterFirstName ?? null}
        initialLastName={adopterDevice?.adopterLastName ?? null}
        initialEmail={adopterDevice?.adopterEmail ?? null}
        initialPhone={adopterDevice?.adopterPhone ?? null}
        recallFirst={adopterDevice?.holderType === 'foster'}
        onTransfer={handleTransferToAdopter}
        onSchedule={async (details, when) => {
          if (adopterDevice) {
            await scheduleHandoff(adopterDevice, 'ADOPTION', when, details, 'devices_t2a');
          }
        }}
      />
      <ResendEmailModal
        open={resendOpen}
        onClose={() => setResendOpen(false)}
        device={resendDevice}
        onResend={handleResendEmail}
      />
      <CancelTransferModal
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
          setCancelDevice(null);
        }}
        device={cancelDevice}
        onConfirm={handleCancelAdoptionTransfer}
        hideStalledOption={cancelHideStalledOption}
      />
      <WrongAdopterInfoModal
        open={reenterOpen}
        onClose={() => {
          setReenterOpen(false);
          setReenterDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been cancelled yet, so nothing is lost.
          setReenterOpen(false);
          setCancelDevice(reenterDevice);
          setCancelOpen(true);
          setReenterDevice(null);
        }}
        device={reenterDevice}
        onConfirm={handleConfirmReenterAdopter}
      />
      <DeviceReturnedModal
        open={returnedOpen}
        onClose={() => {
          setReturnedOpen(false);
          setReturnedDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been recovered yet, so nothing is lost.
          setReturnedOpen(false);
          setCancelDevice(returnedDevice);
          setCancelOpen(true);
          setReturnedDevice(null);
        }}
        device={returnedDevice}
        onConfirm={handleConfirmDeviceReturned}
      />
      <DeviceKeptModal
        open={keptOpen}
        onClose={() => {
          setKeptOpen(false);
          setKeptDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been resolved yet, so nothing is lost.
          setKeptOpen(false);
          setCancelDevice(keptDevice);
          setCancelOpen(true);
          setKeptDevice(null);
        }}
        device={keptDevice}
        onConfirm={handleConfirmDeviceKept}
      />
      <TestingCancelModal
        open={testingOpen}
        onClose={() => {
          setTestingOpen(false);
          setTestingDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been cancelled yet, so nothing is lost.
          setTestingOpen(false);
          setCancelDevice(testingDevice);
          setCancelOpen(true);
          setTestingDevice(null);
        }}
        device={testingDevice}
        onConfirm={handleConfirmTestingCancel}
      />
      <PetAndDeviceReturnedCancelModal
        open={petDeviceReturnedOpen}
        onClose={() => {
          setPetDeviceReturnedOpen(false);
          setPetDeviceReturnedDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been cancelled yet, so nothing is lost.
          setPetDeviceReturnedOpen(false);
          setCancelDevice(petDeviceReturnedDevice);
          setCancelOpen(true);
          setPetDeviceReturnedDevice(null);
        }}
        device={petDeviceReturnedDevice}
        onConfirm={handleConfirmPetAndDeviceReturned}
      />
      <SendSmsModal
        open={smsOpen}
        onClose={() => setSmsOpen(false)}
        device={smsDevice}
        orgName={orgName}
        onSendSms={handleSendSms}
      />
      <MarkRecoveredModal
        open={markRecoveredOpen}
        onClose={() => {
          setMarkRecoveredOpen(false);
          setMarkRecoveredDevice(null);
        }}
        device={markRecoveredDevice}
        onConfirm={handleConfirmMarkRecovered}
      />
      <NotRecoveredModal
        open={notRecoveredOpen}
        onClose={() => {
          setNotRecoveredOpen(false);
          setNotRecoveredDevice(null);
        }}
        device={notRecoveredDevice}
        onConfirm={handleConfirmNotRecovered}
      />
      <AddDeviceToPetModal
        open={assignPetOpen}
        onClose={() => {
          setAssignPetOpen(false);
          setAssignPetModuleId(null);
        }}
        moduleId={assignPetModuleId}
      />
      <OrderDevicesModal open={orderDevicesOpen} onClose={() => setOrderDevicesOpen(false)} />
    </>
  );
}
